import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentIntent, Stripe, StripeElements, StripeElementsOptions } from '@stripe/stripe-js';

import Button from '../../../Forms/Button';
import './Account.payment.payIntent.scss';
import StripeLogo from './assets/stripe_blurple';
import { numberToCurrencyString } from '../../../../services/formatting';

interface PaymentFormProps {
    onSuccess: (paymentIntent: PaymentIntent) => void;
    onCancel: () => void;
}

function PaymentForm({ onSuccess, onCancel }: PaymentFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js has not yet loaded.
            return;
        }

        setLoading(true);
        setErrorMessage('');

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                // @ts-ignore (Requires updating TS version)
                elements: elements as StripeElements,
                confirmParams: {
                    return_url: window.location.origin + '/payment/confirm',
                },
                // @ts-ignore (Requires updating TS version)
                redirect: 'if_required',
            });

            if (error) {
                setErrorMessage(error.message || 'Ocurrió un error al procesar el pago');
                setLoading(false);
            } else {
                // Payment successful
                onSuccess(paymentIntent);
            }
        } catch (err) {
            console.error('Error confirming payment:', err);
            setErrorMessage('Ocurrió un error inesperado. Por favor intenta de nuevo.');
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="paymentForm">
            <PaymentElement />
            
            {errorMessage && (
                <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>
                    <p>
                    {errorMessage}
                    </p>
                </div>
            )}

            <div className='actionButtons'>
                <Button 
                    type="submit" 
                    size='small'
                    primary={true} 
                    disabled={!stripe || loading}
                >
                    <span>{loading ? 'Procesando...' : 'Pagar'}</span>
                </Button>
                <Button 
                    type="button" 
                    size='small' 
                    tertiary={true} 
                    handleClick={onCancel}
                    disabled={loading}
                >
                    <span>Cancelar</span>
                </Button>
            </div>
        </form>
    );
}

interface AccountPaymentPayIntentProps {
    clientSecret: string;
    amountCents: number;
    stripePromise: Promise<Stripe | null>;
    onSuccess: (paymentIntent: PaymentIntent) => void;
    onCancel: () => void;
}

function AccountPaymentPayIntent({ 
    clientSecret, 
    amountCents,
    stripePromise, 
    onSuccess, 
    onCancel 
}: AccountPaymentPayIntentProps) {
    const options: StripeElementsOptions = {
        clientSecret,
        appearance: {
            theme: 'stripe' as const,
            variables: {
                colorText: '#4d4f5c',
                colorPrimary: '#ac2e78',
            }
        },
        loader: 'auto' as const,
    };

    return (
        <div id="AccountPaymentPayIntent">
            <div className="title">
                <StripeLogo width={90} className='gatewayIcon'/>
            </div>
            <div className='orderSummary'>
                <p>
                    <span className='subtitle'>Monto a pagar: <br /></span>
                    ${numberToCurrencyString(amountCents / 100)} MXN
                </p>
            </div>
            <Elements stripe={stripePromise} options={options}>
                <PaymentForm onSuccess={onSuccess} onCancel={onCancel} />
            </Elements>
        </div>
    );
}

export default AccountPaymentPayIntent;
